import { useMutation, useQuery } from '@apollo/client/react';
import { Badge, Button, Heading, SimpleGrid, Text, VStack } from '@chakra-ui/react';
import { SeatCard } from '@src/components/sco/Advisors/cards/SeatCard';
import { useAdvisorsCollectionLoadMore } from '@src/components/sco/Advisors/hooks/useAdvisorsCollectionLoadMore';
import { useBoardSeatCounts } from '@src/components/sco/Advisors/hooks/useBoardSeatCounts';
import { BoardSeatsLayout } from '@src/components/sco/Advisors/layout/BoardSeatsLayout';
import { useAdvisorsPaths } from '@src/components/sco/Advisors/paths';
import { AdvisorsLoadMore } from '@src/components/sco/Advisors/shared/AdvisorsLoadMore';
import { AdvisorsQueryState } from '@src/components/sco/Advisors/shared/AdvisorsQueryState';
import { END_BOARD_SEAT_CONFIRM } from '@src/components/sco/Advisors/utils/engagementLifecycle';
import { engagementToSeatData } from '@src/components/sco/Advisors/utils/engagementMappers';
import { seatsNavItems, SeatsSectionKey } from '@src/components/sco/Advisors/utils/seatsNav';
import { NextLink } from '@src/components/sco/utilities/NextLink';
import { END_ADVISOR_ENGAGEMENT } from '@src/gql/mutations/EndAdvisorEngagement';
import { BOARD_SEAT_COUNTS } from '@src/gql/queries/BoardSeatCounts';
import { BOARD_SEATS_ENGAGEMENTS } from '@src/gql/queries/BoardSeatsEngagements';
import { ScoAdvisorEngagement, ScoAdvisorEngagementCollection } from '@src/graphql-types';
import { useRouter } from 'next/router';
import { useEffect, useState } from 'react';
import { LuPlus } from 'react-icons/lu';
const PAGE_SIZE = 50;
type SeatsData = {
  advisorEngagements?: ScoAdvisorEngagementCollection | null;
};

/** Copy for the empty state, per status view. */
const EMPTY_COPY: Record<Exclude<SeatsSectionKey, 'board'>, {
  title: string;
  body: string;
}> = {
  all: {
    title: 'No board seats yet',
    body: "Create a seat to describe the advisor you need. We'll notify matching members when you publish it. Ended seats also show up here."
  },
  drafts: {
    title: 'No drafts',
    body: 'Seats you start but do not publish wait here until you are ready to match candidates.'
  },
  searching: {
    title: 'Nothing searching right now',
    body: 'Publish a draft seat to start matching it against advisors.'
  },
  invited: {
    title: 'No outstanding invites',
    body: 'When you invite an advisor to a seat, it moves here until they accept or decline.'
  }
};

/**
 * My Advisors recruiting / All views — one status per page, SeatCards only.
 *
 * On your board (people cards) is `MyAdvisorsSection`. Default `all` passes no
 * status filter so every founder-owned seat (including ended) is listed.
 */
export const BoardSeatsSection = ({
  sectionKey = 'all'
}: {
  sectionKey?: Exclude<SeatsSectionKey, 'board'>;
}) => {
  const paths = useAdvisorsPaths();
  const router = useRouter();
  const {
    refetch: refetchCounts
  } = useBoardSeatCounts();
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [showAddedBanner, setShowAddedBanner] = useState(false);
  const navItem = seatsNavItems(paths).find(item => item.key === sectionKey);
  const statusKeys = navItem?.statusKeys ?? [];
  const listVariables = {
    statusKeys,
    pageSize: PAGE_SIZE,
    page: 1
  };
  const query = useQuery<SeatsData>(BOARD_SEATS_ENGAGEMENTS, {
    variables: listVariables,
    fetchPolicy: 'network-only'
  });
  const firstPageSeats = (query.data?.advisorEngagements?.items ?? []).filter((item_0): item_0 is ScoAdvisorEngagement => !!item_0?.advisorEngagementId);
  const {
    items: seats,
    loadMore,
    loadingMore,
    hasNextPage
  } = useAdvisorsCollectionLoadMore({
    fetchMore: query.fetchMore,
    field: 'advisorEngagements',
    firstPageItems: firstPageSeats,
    hasNextPage: query.data?.advisorEngagements?.pagination?.hasNextPage,
    resetKey: statusKeys.join(',')
  });
  const [endAdvisorEngagement, endMutation] = useMutation(END_ADVISOR_ENGAGEMENT, {
    refetchQueries: [{
      query: BOARD_SEATS_ENGAGEMENTS,
      variables: listVariables
    }, {
      query: BOARD_SEAT_COUNTS
    }]
  });
  const seatsWithApplications = seats.filter(seat => seat.statusKey === 'searching' && !seat.advisorUserId && (seat.candidateCount ?? 0) > 0);
  const pendingApplicationCount = seatsWithApplications.reduce((sum, seat_0) => sum + (seat_0.candidateCount ?? 0), 0);
  useEffect(() => {
    if (router.query.added === '1') {
      setShowAddedBanner(true);
      const timeout = window.setTimeout(() => setShowAddedBanner(false), 5000);
      return () => window.clearTimeout(timeout);
    }
  }, [router.query.added]);
  const handleDelete = async (engagementId: string) => {
    if (!window.confirm(END_BOARD_SEAT_CONFIRM)) {
      return;
    }
    setDeletingId(engagementId);
    try {
      await endAdvisorEngagement({
        variables: {
          input: {
            advisorEngagementId: engagementId
          }
        }
      });
      void refetchCounts();
    } finally {
      setDeletingId(null);
    }
  };
  const empty = EMPTY_COPY[sectionKey];
  return <BoardSeatsLayout activeKey={sectionKey} description={navItem?.description} title={navItem?.title ?? 'My Advisors'} action={<Button asChild size={'sm'} variant={'cta'}>
          <NextLink href={paths.seatNew}>
            <LuPlus /> Add a board seat
          </NextLink>
        </Button>}>
      {showAddedBanner && <Badge colorPalette={'green'} variant={'subtle'} width={'fit-content'}>
          Board seat added — we&apos;re matching candidates now.
        </Badge>}
      {pendingApplicationCount > 0 ? <Badge colorPalette={'orange'} variant={'subtle'} width={'fit-content'}>
          {pendingApplicationCount === 1 ? '1 application waiting' : `${pendingApplicationCount} applications waiting`}
        </Badge> : null}

      <AdvisorsQueryState error={query.error} loading={query.loading && !query.data}>
        {seats.length === 0 ? <VStack align={'stretch'} borderColor={'colorPalette.border'} borderRadius={'xl'} borderStyle={'dashed'} borderWidth={'1px'} gap={3} padding={8} textAlign={'center'}>
            <Heading size={'lg'}>{empty.title}</Heading>
            <Text color={'colorPalette.fg/60'} fontSize={'sm'}>
              {empty.body}
            </Text>
            <Button asChild alignSelf={'center'} size={'sm'} variant={'cta'}>
              <NextLink href={paths.seatNew}>
                <LuPlus /> Add a board seat
              </NextLink>
            </Button>
          </VStack> : <>
            <SimpleGrid columns={{
          base: 1,
          md: 2
        }} gap={6} marginTop={2}>
              {seats.map(seat_1 => <SeatCard key={seat_1.advisorEngagementId} canEdit={seat_1.currentUserCanEdit !== false} engagement={seat_1} engagementId={seat_1.advisorEngagementId!} isDeleting={deletingId === seat_1.advisorEngagementId || endMutation.loading} seat={engagementToSeatData(seat_1)} seatId={seat_1.advisorEngagementId!} onDelete={() => handleDelete(seat_1.advisorEngagementId!)} />)}
            </SimpleGrid>
            <AdvisorsLoadMore hasNextPage={hasNextPage} loading={loadingMore} onLoadMore={loadMore} />
          </>}
      </AdvisorsQueryState>
    </BoardSeatsLayout>;
};